import type { SortDescriptor } from "react-stately";

import { Link2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import type { BadgeVariant } from "@/components/badge";

import {
  type BulkOperation,
  BulkOperationsToolbar,
} from "@/components/bulk-operations-toolbar";
import { STATUS_OPTIONS } from "@/features/shared/constants/statusColors";

import type { TeamLeadFilterType } from "../../TeamLeadDropdownFilter";

import {
  assignedToMeTableConfig,
  awaitingReplyTableConfig,
  inQueueTableConfig,
  othersTableConfig,
  processingTableConfig,
  teamleadCompletedTableConfig,
  teamleadDueTodayTableConfig,
  teamleadOverdueTableConfig,
} from "../../../config/tables/teamlead";
import { useBulkChangeStatus, useBulkCopyLinks } from "../../../hooks";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import {
  useTeamLeadRequestCounts,
  useTeamLeadRequestData,
  useTeamLeadSectionStates,
} from "../../../hooks/teamlead";
import { BulkChangeStatusDropdown } from "../../BulkChangeStatusDropdown";
import { BulkChangeStatusModal } from "../../modals/BulkChangeStatusModal";
import { LazyConfigurableTable } from "../../tables/LazyConfigurableTable";

interface AssignedToMeTabProps {
  onFilterChange?: (filter: TeamLeadFilterType) => void;
  searchValue?: string;
  selectedFilter?: TeamLeadFilterType;
  userId?: string;
}

type SectionType = "awaiting-reply" | "in-queue" | "others" | "processing";

const SECTIONS: { id: SectionType; title: string }[] = [
  { id: "processing", title: "Processing" },
  { id: "awaiting-reply", title: "Awaiting Reply" },
  { id: "in-queue", title: "In Queue" },
  { id: "others", title: "Others" },
];

// Default sort configurations for single-table views
const SINGLE_TABLE_SORT_DEFAULTS: Record<
  "completed" | "due-today" | "overdue",
  { column: string; direction: "ascending" | "descending" }
> = {
  completed: {
    column: "completedDate",
    direction: "descending",
  },
  "due-today": {
    column: "dueDate",
    direction: "ascending",
  },
  overdue: {
    column: "dueDate",
    direction: "ascending",
  },
};

// Get section-specific table config
function getSectionTableConfig(sectionId: SectionType) {
  switch (sectionId) {
    case "processing": {
      return processingTableConfig;
    }
    case "awaiting-reply": {
      return awaitingReplyTableConfig;
    }
    case "in-queue": {
      return inQueueTableConfig;
    }
    case "others": {
      return othersTableConfig;
    }
    default: {
      return assignedToMeTableConfig;
    }
  }
}

function AssignedToMeTabComponent({
  onFilterChange,
  searchValue = "",
  selectedFilter = "open",
  userId,
}: AssignedToMeTabProps) {
  const { data: countsData, isLoading: isCountsLoading } =
    useTeamLeadRequestCounts({ search: searchValue, userId });

  // Determine if we should use single-table mode
  const isSingleTableMode =
    selectedFilter === "completed" ||
    selectedFilter === "due-today" ||
    selectedFilter === "overdue";

  // Manage independent state for each section (each has its own default sort)
  const { createSectionHandlers, sectionStates } = useTeamLeadSectionStates();

  // Manage state for single-table view
  const [singleTableState, setSingleTableState] = useState<{
    currentPage: number;
    itemsPerPage: number;
    sortDescriptor: SortDescriptor;
  }>({
    currentPage: 1,
    itemsPerPage: 10,
    sortDescriptor:
      SINGLE_TABLE_SORT_DEFAULTS[
        selectedFilter as "completed" | "due-today" | "overdue"
      ] || SINGLE_TABLE_SORT_DEFAULTS.completed,
  });

  const [cachedTotalPages, setCachedTotalPages] = useState<null | number>(null);
  const [cachedTotalItems, setCachedTotalItems] = useState<null | number>(null);

  // Single table handlers
  const handleSingleTablePageChange = useCallback((page: number) => {
    setSingleTableState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  const handleSingleTableItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setSingleTableState((prev) => ({
        ...prev,
        currentPage: 1,
        itemsPerPage,
      }));
    },
    [],
  );

  const handleSingleTableSortChange = useCallback(
    (sortDescriptor: SortDescriptor) => {
      setSingleTableState((prev) => ({
        ...prev,
        sortDescriptor,
      }));
    },
    [],
  );

  // Bulk operations state
  const [selectedItems, setSelectedItems] = useState<Set<number | string>>(
    new Set(),
  );
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    selectedRequests: Array<{ id: number; name: string }>;
    statusId: number;
    statusLabel: string;
    statusVariant: BadgeVariant;
  } | null>(null);

  // Bulk operations hooks
  const bulkCopyLinksMutation = useBulkCopyLinks({
    onSuccess: () => {
      setSelectedItems(new Set());
    },
  });

  const bulkChangeStatusMutation = useBulkChangeStatus({
    onSuccess: () => {
      setSelectedItems(new Set());
      setPendingStatusChange(null);
    },
  });

  // Fetch single-table data if in single-table mode
  const singleTableData = useTeamLeadRequestData({
    direction:
      singleTableState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: isSingleTableMode,
    filter: selectedFilter,
    limit: singleTableState.itemsPerPage,
    page: singleTableState.currentPage,
    search: searchValue,
    skipCount: false,
    sortBy: singleTableState.sortDescriptor.column as string,
    tab: "assigned-to-me",
    userId,
    viewMode: "single-table",
  });

  useEffect(() => {
    if (singleTableState.currentPage === 1) {
      if (singleTableData.data?.pagination?.totalPages) {
        setCachedTotalPages(singleTableData.data.pagination.totalPages);
      }
      if (singleTableData.data?.total != null) {
        setCachedTotalItems(singleTableData.data.total);
      }
    }
  }, [
    singleTableState.currentPage,
    singleTableData.data?.pagination?.totalPages,
    singleTableData.data?.total,
  ]);

  useEffect(() => {
    setSingleTableState((prev) => ({ ...prev, currentPage: 1 }));
    setCachedTotalPages(null);
    setCachedTotalItems(null);
  }, [selectedFilter, searchValue]);

  // Fetch data for all sections at the top level (React Hooks rules)
  // Only fetch section data if NOT in single-table mode
  const processingState = sectionStates.processing;
  const awaitingReplyState = sectionStates["awaiting-reply"];
  const inQueueState = sectionStates["in-queue"];
  const othersState = sectionStates.others;

  const processingData = useTeamLeadRequestData({
    direction:
      processingState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: processingState.itemsPerPage,
    page: processingState.currentPage,
    search: searchValue,
    section: "processing",
    skipCount: false, // Always get count from data query
    sortBy: processingState.sortDescriptor.column as string,
    tab: "assigned-to-me",
    userId,
    viewMode: "section",
  });

  const awaitingReplyData = useTeamLeadRequestData({
    direction:
      awaitingReplyState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: awaitingReplyState.itemsPerPage,
    page: awaitingReplyState.currentPage,
    search: searchValue,
    section: "awaiting-reply",
    skipCount: false, // Always get count from data query
    sortBy: awaitingReplyState.sortDescriptor.column as string,
    tab: "assigned-to-me",
    userId,
    viewMode: "section",
  });

  const inQueueData = useTeamLeadRequestData({
    direction:
      inQueueState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: inQueueState.itemsPerPage,
    page: inQueueState.currentPage,
    search: searchValue,
    section: "in-queue",
    skipCount: false, // Always get count from data query
    sortBy: inQueueState.sortDescriptor.column as string,
    tab: "assigned-to-me",
    userId,
    viewMode: "section",
  });

  const othersData = useTeamLeadRequestData({
    direction:
      othersState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: othersState.itemsPerPage,
    page: othersState.currentPage,
    search: searchValue,
    section: "others",
    skipCount: false, // Always get count from data query
    sortBy: othersState.sortDescriptor.column as string,
    tab: "assigned-to-me",
    userId,
    viewMode: "section",
  });

  const sectionQueries = [
    processingData,
    awaitingReplyData,
    inQueueData,
    othersData,
  ];

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All requests" is), so always escalate.
    isAlreadyBroadest: false,
    isReady: isSingleTableMode
      ? singleTableData.isSuccess && !singleTableData.isFetching
      : sectionQueries.every((q) => q.isSuccess && !q.isFetching),
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: isSingleTableMode
      ? (singleTableData.data?.total ?? 0)
      : sectionQueries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0),
  });

  // Helper function to convert between request ID and table row ID formats
  const getTableRowId = useCallback(
    (requestId: number | string, configId: string): string =>
      `${configId}-${requestId}`,
    [],
  );

  const getRequestIdFromTableRowId = useCallback(
    (tableRowId: string): string => {
      // Table row IDs are formatted as: "config-id-segments-requestId"
      // e.g., "assigned-to-me-processing-123"
      // We need to extract just the last segment (the request ID)
      const parts = tableRowId.split("-");

      return parts.at(-1) ?? "";
    },
    [],
  );

  // Bulk operations handlers
  const handleBulkCopyLinks = useCallback(() => {
    const selectedIds = [...selectedItems]
      .map((id) => getRequestIdFromTableRowId(String(id)))
      .map(Number)
      .filter((id) => !Number.isNaN(id) && id > 0);

    if (selectedIds.length === 0) return;

    bulkCopyLinksMutation.mutate(selectedIds);
  }, [selectedItems, getRequestIdFromTableRowId, bulkCopyLinksMutation]);

  const handleStatusSelect = useCallback(
    (statusId: number, statusLabel: string) => {
      let selectedRequests: Array<{ id: number; name: string }> = [];

      if (isSingleTableMode) {
        // Single table mode - all requests use the same config
        const allRequests = singleTableData.data?.requests || [];
        const tableConfig = getSingleTableConfig();

        selectedRequests = allRequests
          .filter((request) =>
            selectedItems.has(getTableRowId(request.id, tableConfig.id)),
          )
          .map((request) => ({
            id: request.id,
            name: request.name,
          }));
      } else {
        // Multi-section mode - each section has its own config
        const sectionsWithRequests = [
          {
            requests: processingData.data?.requests || [],
            sectionId: "processing" as SectionType,
          },
          {
            requests: awaitingReplyData.data?.requests || [],
            sectionId: "awaiting-reply" as SectionType,
          },
          {
            requests: inQueueData.data?.requests || [],
            sectionId: "in-queue" as SectionType,
          },
          {
            requests: othersData.data?.requests || [],
            sectionId: "others" as SectionType,
          },
        ];

        selectedRequests = sectionsWithRequests.flatMap(
          ({ requests, sectionId }) => {
            const configId = `assigned-to-me-${sectionId}`;

            return requests
              .filter((request) =>
                selectedItems.has(getTableRowId(request.id, configId)),
              )
              .map((request) => ({
                id: request.id,
                name: request.name,
              }));
          },
        );
      }

      if (selectedRequests.length === 0) return;

      // Find status variant for badge display
      const statusOption = STATUS_OPTIONS.find((opt) => opt.id === statusId);
      const statusVariant = statusOption?.variant || "primary";

      setPendingStatusChange({
        selectedRequests,
        statusId,
        statusLabel,
        statusVariant,
      });
    },
    [
      selectedItems,
      isSingleTableMode,
      singleTableData,
      processingData,
      awaitingReplyData,
      inQueueData,
      othersData,
      getTableRowId,
    ],
  );

  const confirmBulkChangeStatus = useCallback(() => {
    if (!pendingStatusChange) return;

    const selectedIds = pendingStatusChange.selectedRequests.map(
      (req) => req.id,
    );

    bulkChangeStatusMutation.mutate({
      requestIds: selectedIds,
      statusId: pendingStatusChange.statusId,
    });
  }, [pendingStatusChange, bulkChangeStatusMutation]);

  const handleSelectAll = useCallback(() => {
    let allCurrentPageIds: string[] = [];

    if (isSingleTableMode) {
      // Single table mode - all requests use the same config
      const allRequests = singleTableData.data?.requests || [];
      const tableConfig = getSingleTableConfig();

      allCurrentPageIds = allRequests.map((request) =>
        getTableRowId(request.id, tableConfig.id),
      );
    } else {
      // Multi-section mode - each section has its own config
      const sectionsWithRequests = [
        {
          requests: processingData.data?.requests || [],
          sectionId: "processing" as SectionType,
        },
        {
          requests: awaitingReplyData.data?.requests || [],
          sectionId: "awaiting-reply" as SectionType,
        },
        {
          requests: inQueueData.data?.requests || [],
          sectionId: "in-queue" as SectionType,
        },
        {
          requests: othersData.data?.requests || [],
          sectionId: "others" as SectionType,
        },
      ];

      allCurrentPageIds = sectionsWithRequests.flatMap(
        ({ requests, sectionId }) => {
          const configId = `assigned-to-me-${sectionId}`;

          return requests.map((request) => getTableRowId(request.id, configId));
        },
      );
    }

    const areAllSelected = allCurrentPageIds.every((id) =>
      selectedItems.has(id),
    );

    if (areAllSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(allCurrentPageIds));
    }
  }, [
    isSingleTableMode,
    singleTableData,
    processingData,
    awaitingReplyData,
    inQueueData,
    othersData,
    selectedItems,
    getTableRowId,
  ]);

  const handleClearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, []);

  const handleSelectionChange = useCallback(
    (newSelection: Set<number | string>) => {
      setSelectedItems(new Set(newSelection));
    },
    [],
  );

  // Define bulk operation
  const bulkOperations: BulkOperation[] = useMemo(
    () => [
      {
        color: "secondary",
        icon: Link2,
        id: "copy-links",
        label: "Copy link",
        onClick: handleBulkCopyLinks,
      },
    ],
    [handleBulkCopyLinks],
  );

  // Keyboard shortcuts for bulk operations
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.contentEditable === "true";

      if (isTyping) return;

      const key = event.key.toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;

      switch (key) {
        case "escape": {
          if (selectedItems.size > 0) {
            event.preventDefault();
            handleClearSelection();
          }
          break;
        }
        case "a": {
          if (hasModifier) {
            event.preventDefault();
            handleSelectAll();
          }
          break;
        }
        default: {
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedItems.size, handleClearSelection, handleSelectAll]);

  // Get appropriate table config based on filter
  const getSingleTableConfig = () => {
    switch (selectedFilter) {
      case "completed": {
        return teamleadCompletedTableConfig;
      }
      case "due-today": {
        return teamleadDueTodayTableConfig;
      }
      case "overdue": {
        return teamleadOverdueTableConfig;
      }
      default: {
        return assignedToMeTableConfig;
      }
    }
  };

  // Render single-table view
  if (isSingleTableMode) {
    const { data, isLoading } = singleTableData;
    const tableConfig = getSingleTableConfig();

    // Get count based on selected filter
    const getCount = (): number | undefined => {
      if (!countsData?.counts.assignedToMe) {
        return undefined;
      }

      return undefined;
    };

    const useDataTotal = searchValue || isSingleTableMode;
    const effectiveTotal = useDataTotal
      ? (data?.total ?? cachedTotalItems)
      : (getCount() ?? null);
    const effectiveCount = useDataTotal ? data?.total : getCount();

    const computedTotalPages = effectiveTotal
      ? Math.ceil(effectiveTotal / singleTableState.itemsPerPage)
      : null;
    const effectiveTotalPages = computedTotalPages ?? cachedTotalPages;

    return (
      <>
        <div className="space-y-6">
          <LazyConfigurableTable
            config={tableConfig}
            count={effectiveCount}
            data={data?.requests || []}
            isCountLoading={isCountsLoading}
            isLoading={isLoading}
            paginationProps={{
              metadata: {
                currentPage: singleTableState.currentPage,
                itemsPerPage: singleTableState.itemsPerPage,
                totalItems: effectiveTotal,
                totalPages: effectiveTotalPages,
              },
              onItemsPerPageChange: handleSingleTableItemsPerPageChange,
              onPageChange: handleSingleTablePageChange,
              pageSizeOptions: [10, 25, 50, 100],
              showPageSizeSelector: true,
            }}
            selectionConfig={{
              enabled: true,
              isLoading:
                bulkCopyLinksMutation.isPending ||
                bulkChangeStatusMutation.isPending,
              key: `selection-${[...selectedItems].join("-")}`,
              onClearSelection: handleClearSelection,
              onSelectAll: handleSelectAll,
              onSelectionChange: handleSelectionChange,
              selectedItems,
            }}
            setSortDescriptor={handleSingleTableSortChange}
            sortDescriptor={singleTableState.sortDescriptor}
            titleActions={
              <BulkOperationsToolbar
                isLoading={
                  bulkCopyLinksMutation.isPending ||
                  bulkChangeStatusMutation.isPending
                }
                operations={bulkOperations}
                selectedCount={selectedItems.size}
                statusDropdown={
                  <BulkChangeStatusDropdown
                    isDisabled={selectedItems.size === 0}
                    isLoading={bulkChangeStatusMutation.isPending}
                    onStatusSelect={handleStatusSelect}
                  />
                }
                variant="compact"
              />
            }
            useAdvancedSorting={true}
          />
        </div>

        {pendingStatusChange && (
          <BulkChangeStatusModal
            isLoading={bulkChangeStatusMutation.isPending}
            isOpen={true}
            newStatusLabel={pendingStatusChange.statusLabel}
            newStatusVariant={pendingStatusChange.statusVariant}
            onConfirm={confirmBulkChangeStatus}
            onOpenChange={(open) => {
              if (!open) setPendingStatusChange(null);
            }}
            selectedRequests={pendingStatusChange.selectedRequests}
          />
        )}
      </>
    );
  }

  // Render multi-section view (for "all" and "open" filters)
  // Map sections to their data
  const sectionDataMap = {
    "awaiting-reply": awaitingReplyData,
    "in-queue": inQueueData,
    others: othersData,
    processing: processingData,
  };

  // Map sections to their counts (stable source for pagination totals)
  const sectionCountsMap: Record<SectionType, number | undefined> = {
    "awaiting-reply": countsData?.counts.assignedToMe.awaitingReply,
    "in-queue": countsData?.counts.assignedToMe.inQueue,
    others: countsData?.counts.assignedToMe.others,
    processing: countsData?.counts.assignedToMe.processing,
  };

  return (
    <>
      <div className="space-y-6">
        {SECTIONS.map((section, index) => {
          const sectionState = sectionStates[section.id];
          const handlers = createSectionHandlers(section.id);
          const { data, isLoading } = sectionDataMap[section.id];
          const isFirstSection = index === 0;
          const sectionConfig = getSectionTableConfig(section.id);

          const effectiveTotal = searchValue
            ? (data?.total ?? null)
            : (sectionCountsMap[section.id] ?? null);
          const effectiveCount = searchValue
            ? data?.total
            : sectionCountsMap[section.id];

          return (
            <div key={section.id}>
              <LazyConfigurableTable
                config={{
                  ...sectionConfig,
                  id: `assigned-to-me-${section.id}`,
                  title: section.title,
                }}
                count={effectiveCount}
                data={data?.requests || []}
                isCountLoading={isCountsLoading}
                isLoading={isLoading}
                paginationProps={{
                  metadata: {
                    currentPage: sectionState.currentPage,
                    itemsPerPage: sectionState.itemsPerPage,
                    totalItems: effectiveTotal,
                    totalPages: effectiveTotal
                      ? Math.ceil(effectiveTotal / sectionState.itemsPerPage)
                      : null,
                  },
                  onItemsPerPageChange: handlers.onItemsPerPageChange,
                  onPageChange: handlers.onPageChange,
                  pageSizeOptions: [10, 25, 50, 100],
                  showPageSizeSelector: true,
                }}
                selectionConfig={{
                  enabled: true,
                  isLoading:
                    bulkCopyLinksMutation.isPending ||
                    bulkChangeStatusMutation.isPending,
                  key: `selection-${section.id}-${[...selectedItems].join("-")}`,
                  onClearSelection: handleClearSelection,
                  onSelectAll: handleSelectAll,
                  onSelectionChange: handleSelectionChange,
                  selectedItems,
                }}
                setSortDescriptor={handlers.onSortChange}
                sortDescriptor={sectionState.sortDescriptor}
                titleActions={
                  isFirstSection ? (
                    <BulkOperationsToolbar
                      isLoading={
                        bulkCopyLinksMutation.isPending ||
                        bulkChangeStatusMutation.isPending
                      }
                      operations={bulkOperations}
                      selectedCount={selectedItems.size}
                      statusDropdown={
                        <BulkChangeStatusDropdown
                          isDisabled={selectedItems.size === 0}
                          isLoading={bulkChangeStatusMutation.isPending}
                          onStatusSelect={handleStatusSelect}
                        />
                      }
                      variant="compact"
                    />
                  ) : undefined
                }
                useAdvancedSorting={true}
              />
            </div>
          );
        })}
      </div>

      {pendingStatusChange && (
        <BulkChangeStatusModal
          isLoading={bulkChangeStatusMutation.isPending}
          isOpen={true}
          newStatusLabel={pendingStatusChange.statusLabel}
          newStatusVariant={pendingStatusChange.statusVariant}
          onConfirm={confirmBulkChangeStatus}
          onOpenChange={(open) => {
            if (!open) setPendingStatusChange(null);
          }}
          selectedRequests={pendingStatusChange.selectedRequests}
        />
      )}
    </>
  );
}

export const AssignedToMeTab = memo(AssignedToMeTabComponent);
